import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';

const ThemeContext = createContext(null);

// Read initial theme from localStorage synchronously to avoid flash
function getInitialTheme(userId) {
  if (!userId) return false;
  return localStorage.getItem(`theme_${userId}`) === 'dark';
}

export function ThemeProvider({ children }) {
  const { user } = useAuth();
  const userId = user?.user_id;

  const [darkMode, setDarkMode] = useState(() => getInitialTheme(userId));
  const [loaded, setLoaded] = useState(false);

  // Sync theme from localStorage immediately when userId changes
  useEffect(() => {
    if (!userId) {
      setDarkMode(false);
      setLoaded(false);
      return;
    }

    // The server's value arrives with the user object (/api/auth/me returns users.theme_preference), so no extra
    // request is needed; localStorage (written by AuthContext before the user state update) avoids a flash.
    const serverTheme = user?.theme_preference;
    const localTheme = localStorage.getItem(`theme_${userId}`);
    const theme = serverTheme || localTheme;
    if (theme) {
      setDarkMode(theme === 'dark');
      if (serverTheme && serverTheme !== localTheme) localStorage.setItem(`theme_${userId}`, serverTheme);
    }
    setLoaded(true);
  }, [userId, user?.theme_preference]);

  // Dark mode is scoped to the dashboard: DashboardLayout puts the 'dark' class on its own wrapper div, and the
  // `dashboard-dark` class on <body> (for Radix popovers portalled outside that wrapper) while it is mounted. Public
  // pages stay light for a signed-in dark-mode user.

  const toggleDarkMode = useCallback(() => {
    setDarkMode(prev => {
      const newVal = !prev;
      if (userId) {
        api.put('/api/preferences', { theme: newVal ? 'dark' : 'light' }).catch(() => {});
        localStorage.setItem(`theme_${userId}`, newVal ? 'dark' : 'light');
      }
      return newVal;
    });
  }, [userId]);

  return (
    <ThemeContext.Provider value={{ darkMode, toggleDarkMode }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be inside ThemeProvider');
  return ctx;
};
